import React, { useState } from 'react';
import { isEmpty } from 'lodash';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { getAllCustomer, getCustomerDetailByIdAPI, mergeCustomerAPI, popup } from '../../../utils';
import SSCSelect from '../../shared/SSCSelect';
import { CircleSpinner, ReplaceIcon } from '../../../assets/svg';
import { ConfirmationBox } from '../../shared';
import { QUERY_KEYS } from '../../../constants';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const AccountMergeModal = ({ showModal, setShowModal, accountUserInfo }) => {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [openConfirmationBox, setOpenConfirmationBox] = useState(false);

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isError, error } = useQuery({
        queryKey: [QUERY_KEYS.CUSTOMERS, debouncedSearch],
        queryFn: async () =>
            await getAllCustomer({
                offset: 1,
                limit: 50,
                search: debouncedSearch,
                withPrimaryUsers: false,
                withPlatforms: false,
                filter: { 'not-source': 'Schedular' },
            }),
        select: (data) =>
            data.data.length > 0
                ? data?.data?.map((customer) => ({ label: customer.name, value: customer }))
                : [],
    });
    if (isError) popup('error', error?.message);

    const { data: customerDetail, isFetching: isDetailFetching } = useQuery({
        queryKey: [QUERY_KEYS.MERGING_ACCOUNT_DETAIL, selectedCustomer?.id],
        queryFn: async () => await getCustomerDetailByIdAPI({ id: selectedCustomer.id }),
        enabled: !!selectedCustomer?.id,
        select: (data) => data.data ?? {},
    });

    const mergeMutation = useMutation({
        mutationFn: async () =>
            await mergeCustomerAPI({
                schedular_id: accountUserInfo.id,
                admin_id: selectedCustomer.id,
            }),
        onSuccess: (res) => {
            popup('success', res.message);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMERS], exact: false });
            setShowModal(false);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const onConfirm = (confirm) => {
        if (confirm && accountUserInfo.id && selectedCustomer.id) {
            mergeMutation.mutate();
        }
    };

    return (
        <Modal
            show={showModal}
            onHide={() => {
                setShowModal(false);
            }}
            size='lg'
            dialogClassName='overflow-hidden'
            // fullscreen='lg-down'
            centered
            backdrop='static'
        >
            <Modal.Header closeButton>
                <Modal.Title
                    id='contained-modal-title-vcenter'
                    className='d-flex align-items-center gap-2 justify-content-start w-100'
                    style={{ fontSize: '16px', textWrap: 'nowrap' }}
                >
                    {`Merge ${accountUserInfo.name}`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className='col-12'>
                    <SSCSelect
                        className={'w-100 w-md-50'}
                        options={data}
                        placeholder={'Search non-schedular accounts...'}
                        onChange={(d) => setSelectedCustomer(d?.value)}
                        onInputChange={(v) => {
                            setSearch(v);
                        }}
                        isLoading={isFetching}
                        isClearable={true}
                        formatOptionLabel={({ label, value }) =>
                            value?.email ? (
                                <div className='d-flex flex-column'>
                                    <span>{label}</span>
                                    <span style={{ fontSize: '12px', color: 'var(--col-subtext)' }}>
                                        {value?.email +
                                            `${value?.source ? `- ${value?.source}` : ''}`}
                                    </span>
                                </div>
                            ) : (
                                <div>{label}</div>
                            )
                        }
                        selectStyle={{ minHeight: '54px' }}
                        isOptionSelected={(option) => option.value?.id === selectedCustomer?.id}
                        value={
                            selectedCustomer
                                ? { label: selectedCustomer?.name, value: selectedCustomer }
                                : null
                        }
                    />
                </div>
                <div className='divider mt-2'></div>
                {isDetailFetching ? (
                    <div className='position-relative w-100 min-h-30'>
                        <CircleSpinner absolute size='2.5rem' />
                    </div>
                ) : !isEmpty(customerDetail) ? (
                    <div className='col-12 mt-2'>
                        <table className=' table table-hover mt-2'>
                            <thead className='table-primary'>
                                <tr>
                                    <th className='bg-primary text-white w-75'>Metrics</th>
                                    <th className='bg-primary text-white w-25 text-end'>Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(customerDetail).map((item) => (
                                    <tr key={item[0]}>
                                        <td className='fw-semibold'>{item[0]}</td>
                                        <td className='text-end pe-4'>{item[1]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <div className='d-flex gap-3 w-100 align-items-center justify-content-end modal-btn mt-3'>
                    <button
                        type='button'
                        className='ssc-secondary-white-btn'
                        onClick={() => setOpenConfirmationBox(true)}
                        style={{ height: '40px', fontSize: '15px' }}
                        aria-label={'Merge Account'}
                        disabled={
                            isDetailFetching ||
                            isFetching ||
                            !selectedCustomer?.id ||
                            mergeMutation.isPending
                        }
                    >
                        {mergeMutation.isPending ? <CircleSpinner /> : <ReplaceIcon />}
                        Merge
                    </button>
                </div>
            </Modal.Footer>
            {openConfirmationBox && (
                <ConfirmationBox
                    setConfirm={(v) => onConfirm(v)}
                    openConfirmationBox={openConfirmationBox}
                    setOpenConfirmationBox={setOpenConfirmationBox}
                    customMessage={
                        <>
                            <span>
                                Are you sure you want to merge{' '}
                                <strong>{selectedCustomer.name}</strong> into{' '}
                                <strong>{accountUserInfo.name}</strong> ?
                            </span>
                            <br />
                            <ol>
                                <li>
                                    Add <strong>{selectedCustomer.name}</strong>&apos;s digital{' '}
                                    deliverables to <strong>{accountUserInfo?.name}</strong>.
                                </li>
                                <li>
                                    Copy all hiring client grades from{' '}
                                    <strong>{selectedCustomer?.name}</strong> to{' '}
                                    <strong>{accountUserInfo.name}</strong>.
                                </li>
                                <li>
                                    Permanently delete <strong>{selectedCustomer?.name}</strong>.
                                </li>
                            </ol>
                        </>
                    }
                />
            )}
        </Modal>
    );
};

AccountMergeModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    setShowModal: PropTypes.func.isRequired,
    debouncedApiCall: PropTypes.func.isRequired,
    accountUserInfo: PropTypes.object.isRequired,
};
